import { useEffect, useState } from 'react';
import { api } from '../../core/apiClient.ts';
import type { LaunchTemplate, LaunchTemplateListItem } from './types.ts';

export type AdAccount = { id: string; name: string | null; currency: string | null };
export type Page = { id: string; name: string | null };
export type Pixel = { id: string; name: string | null };
export type Post = { id: string; message: string | null };
export type Payer = { beneficiaryName: string | null; payerName: string | null };
export type WizardTarget = { ad_account_id: string; page_id: string; pixel_id: string | null; post_id: string; link_url: string; budget_override: number | null; beneficiary_name: string; payer_name: string; url_tags: string; custom_name: string; scheduled_at: string | null };

export function useLaunchWizard() {
  const [templates, setTemplates] = useState<LaunchTemplateListItem[]>([]);
  const [adAccounts, setAdAccounts] = useState<AdAccount[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [pixels, setPixels] = useState<Record<string, Pixel[]>>({});
  const [posts, setPosts] = useState<Record<string, Post[]>>({});
  const [payers, setPayers] = useState<Record<string, Payer[]>>({});
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [templateBudget, setTemplateBudget] = useState<number | null>(null);
  const [scheduledAt, setScheduledAt] = useState<string | null>(null);
  const [pauseSeconds, setPauseSeconds] = useState(30);
  const [targets, setTargets] = useState<Record<string, WizardTarget>>({});
  // selectedOrder tracks the order in which accounts were selected
  const [selectedOrder, setSelectedOrder] = useState<string[]>([]);
  const [adsetsCount, setAdsetsCount] = useState(1);
  const [adsPerAdset, setAdsPerAdset] = useState(1);

  useEffect(() => {
    Promise.all([
      api.get<LaunchTemplateListItem[]>('/api/launch-templates'),
      api.get<AdAccount[]>('/api/launcher/ad-accounts'),
      api.get<Page[]>('/api/launcher/pages'),
    ]).then(([t, a, p]) => { setTemplates(t); setAdAccounts(a); setPages(p); }).catch(console.error);
  }, []);

  const loadPixels = async (accountId: string) => {
    try {
      const data = await api.get<Pixel[]>(`/api/launcher/pixels/${accountId}`);
      setPixels((old) => ({ ...old, [accountId]: data }));
      if (data.length > 0) {
        setTargets((old) => {
          if (old[accountId] && !old[accountId].pixel_id) {
            return { ...old, [accountId]: { ...old[accountId], pixel_id: data[0].id } };
          }
          return old;
        });
      }
    } catch (err) {
      console.error('Failed to load pixels:', err);
    }
  };
  const loadPosts = async (pageId: string) => {
    if (!posts[pageId]) {
      const data = await api.get<Post[]>(`/api/launcher/posts/${pageId}`);
      setPosts((old) => ({ ...old, [pageId]: data }));
    }
  };
  const loadPayers = async (accountId: string) => {
    if (!payers[accountId]) {
      const data = await api.get<Payer[]>(`/api/launcher/payers/${accountId}`);
      setPayers((old) => ({ ...old, [accountId]: data }));
    }
  };

  const chooseTemplate = async (id: number | null) => {
    setTemplateId(id);
    setTemplateBudget(null);
    if (id) {
      const template = await api.get<LaunchTemplate>(`/api/launch-templates/${id}`);
      setTemplateBudget(template.config.campaign.budget_amount);
    }
  };

  const selectAccount = (accountId: string, selected: boolean, budget: number | null, globalScheduledAt?: string | null) => {
    setTargets((old) => {
      const next = { ...old };
      if (selected) next[accountId] = { ad_account_id: accountId, page_id: '', pixel_id: null, post_id: '', link_url: '', budget_override: budget, beneficiary_name: '', payer_name: '', url_tags: '', custom_name: '', scheduled_at: globalScheduledAt ?? null };
      else delete next[accountId];
      return next;
    });
    setSelectedOrder((old) => {
      if (selected) return old.includes(accountId) ? old : [...old, accountId];
      return old.filter((id) => id !== accountId);
    });
    if (selected) {
      loadPixels(accountId);
    }
  };

  // Bulk-select multiple accounts at once (from Bulk Accs modal)
  const bulkSelectAccounts = (accountIds: string[], budget: number | null, globalScheduledAt?: string | null) => {
    setTargets((old) => {
      const next = { ...old };
      for (const accountId of accountIds) {
        if (!next[accountId]) {
          next[accountId] = { ad_account_id: accountId, page_id: '', pixel_id: null, post_id: '', link_url: '', budget_override: budget, beneficiary_name: '', payer_name: '', url_tags: '', custom_name: '', scheduled_at: globalScheduledAt ?? null };
        }
      }
      return next;
    });
    setSelectedOrder((old) => {
      const result = [...old];
      for (const id of accountIds) {
        if (!result.includes(id)) result.push(id);
      }
      return result;
    });
    accountIds.forEach((id) => loadPixels(id));
  };

  const updateTarget = (accountId: string, patch: Partial<WizardTarget>) =>
    setTargets((old) => ({ ...old, [accountId]: { ...old[accountId], ...patch } }));

  const submitBatch = async () => {
    if (!templateId) throw new Error('Select a template');
    const selected = Object.values(targets);
    if (!selected.length || selected.some((target) => !target.page_id || !target.post_id || !target.pixel_id)) {
      throw new Error('Select a page, pixel, and post for every account');
    }
    return api.post<{ batch_run_id: string; launches_count: number }>('/api/launcher/batches', {
      template_id: templateId,
      scheduled_at: scheduledAt,
      pause_seconds: pauseSeconds,
      adsets_count: adsetsCount,
      ads_per_adset: adsPerAdset,
      targets: selected.map((t) => ({ ...t, custom_name: t.custom_name.trim() || null, scheduled_at: t.scheduled_at || null })),
    });
  };

  const reset = () => {
    setTemplateId(null);
    setTemplateBudget(null);
    setScheduledAt(null);
    setPauseSeconds(30);
    setTargets({});
    setSelectedOrder([]);
    setAdsetsCount(1);
    setAdsPerAdset(1);
  };

  return {
    templates, adAccounts, pages, pixels, posts, payers,
    templateId, templateBudget, setTemplateId: chooseTemplate,
    scheduledAt, setScheduledAt, pauseSeconds, setPauseSeconds,
    targets, selectedOrder, selectAccount, bulkSelectAccounts, updateTarget, loadPixels, loadPosts, loadPayers,
    adsetsCount, setAdsetsCount, adsPerAdset, setAdsPerAdset,
    submitBatch, reset,
  };
}
